import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const teal = "#06d1c6";

const PENDING_COLUMNS = [
  { key: "clientCreatedAt", label: "Client Created At", sort: true, search: true },
  { key: "clientName", label: "Client Name", sort: true, search: true },
  { key: "email", label: "Email", sort: true, search: true },
  { key: "relationshipManager", label: "Relationship Manager", sort: true, search: true },
  { key: "subBroker", label: "Sub Broker", sort: true, search: true },
  { key: "aum", label: "AUM", sort: true, search: true },
  { key: "status", label: "Status", sort: true, search: true },
];

const LOGS_COLUMNS = [
  { key: "dateOfSubmission", label: "Date of Submission", sort: true, search: true },
  { key: "clientName", label: "Client Name", sort: true, search: true },
  { key: "email", label: "Email", sort: true, search: true },
  { key: "relationshipManager", label: "Relationship Manager", sort: true, search: true },
  { key: "subBroker", label: "Sub Broker", sort: true, search: true },
  { key: "aum", label: "AUM", sort: true, search: true },
  { key: "riskCategory", label: "Risk Category", sort: true, search: true },
  { key: "suggestedRiskCategory", label: "Suggested Risk Category", sort: true, search: true },
];

export default function Submissions() {
  const [activeTab, setActiveTab] = useState("pending"); // "pending" | "logs"
  const [sortBy, setSortBy] = useState(activeTab === "pending" ? "clientName" : "dateOfSubmission");
  const [sortDir, setSortDir] = useState(activeTab === "pending" ? "asc" : "desc");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllNonZeroAum, setSelectAllNonZeroAum] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const pendingCount = 0;
  const logsCount = 0;
  const pendingRows = [];
  const logsRows = [];

  useEffect(() => {
    if (activeTab === "pending") {
      setSortBy("clientName");
      setSortDir("asc");
    } else {
      setSortBy("dateOfSubmission");
      setSortDir("desc");
    }
  }, [activeTab]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir(activeTab === "pending" ? "asc" : "desc");
    }
  };

  const toggleSelectAll = (e) => {
    const rows = activeTab === "pending" ? pendingRows : [];
    if (e.target.checked) setSelectedIds(new Set(rows.map((r) => r.id)));
    else setSelectedIds(new Set());
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const SortIcon = ({ columnKey }) => (
    <button
      type="button"
      className="btn btn-link p-0 ms-1 border-0"
      style={{ color: sortBy === columnKey ? teal : "#64748b", fontSize: "11px" }}
      onClick={() => toggleSort(columnKey)}
      aria-label={`Sort by ${columnKey}`}
    >
      <i className={`fa fa-sort-${sortBy === columnKey ? (sortDir === "asc" ? "up" : "down") : "up"}`} style={{ opacity: sortBy === columnKey ? 1 : 0.4 }} />
    </button>
  );

  const displayRows = activeTab === "pending" ? pendingRows : logsRows;
  const totalRows = activeTab === "pending" ? pendingCount : logsCount;
  const paginatedRows = displayRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          {/* Breadcrumb and Header */}
          <div className="mb-4" style={cardStyle}>
            <div className="content-page-header content-page-headersplit">
              <h5>Submissions</h5>
            </div>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/dashboard">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <NavLink to="/admin/risk-profiling/submissions">Risk Profiling</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Submissions
                </li>
              </ol>
            </nav>
          </div>

          {/* Tabs and actions */}
          <div style={cardStyle}>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
              <div className="d-flex">
                <button
                  type="button"
                  onClick={() => setActiveTab("pending")}
                  className="border-0 bg-transparent pb-2 px-0 me-4"
                  style={{
                    fontWeight: 600,
                    fontSize: "15px",
                    color: activeTab === "pending" ? teal : "#94a3b8",
                    borderBottom: activeTab === "pending" ? `3px solid ${teal}` : "3px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  Pending / Expired : {pendingCount}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("logs")}
                  className="border-0 bg-transparent pb-2 px-0"
                  style={{
                    fontWeight: 600,
                    fontSize: "15px",
                    color: activeTab === "logs" ? teal : "#94a3b8",
                    borderBottom: activeTab === "logs" ? `3px solid ${teal}` : "3px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  Submission Logs : {logsCount}
                </button>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {activeTab === "pending" && (
                  <>
                    <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer", fontSize: "14px" }}>
                      <input type="checkbox" checked={pendingRows.length > 0 && selectedIds.size === pendingRows.length} onChange={toggleSelectAll} style={{ accentColor: teal }} />
                      Select All
                    </label>
                    <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer", fontSize: "14px" }}>
                      <input type="checkbox" checked={selectAllNonZeroAum} onChange={(e) => setSelectAllNonZeroAum(e.target.checked)} style={{ accentColor: teal }} />
                      Select All Non Zero AUM Clients
                    </label>
                    <button type="button" className="d-flex align-items-center justify-content-center border-0 rounded" style={{ width: 36, height: 36, background: "#f1f5f9", color: "#64748b", cursor: "pointer" }} title="Refresh">
                      <i className="fa-solid fa-sync-alt" />
                    </button>
                    <button
                      type="button"
                      style={{
                        background: teal,
                        color: "#fff",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      Send Link
                    </button>
                  </>
                )}
                {activeTab === "logs" && (
                  <button type="button" className="d-flex align-items-center justify-content-center border-0 rounded" style={{ width: 36, height: 36, background: "#f1f5f9", color: "#64748b", cursor: "pointer" }} title="Refresh">
                    <i className="fa-solid fa-sync-alt" />
                  </button>
                )}
                <button type="button" className="d-flex align-items-center justify-content-center border-0 rounded" style={{ width: 40, height: 40, background: "#f1f5f9", color: "#64748b", cursor: "pointer" }} title="Export to XLS">
                  <i className="fa fa-file-excel" style={{ fontSize: "18px" }} />
                </button>
              </div>
            </div>

            {/* Table - Pending / Expired */}
            {activeTab === "pending" && (
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered mb-0" style={{ fontSize: "13px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      <th style={{ padding: "10px 12px", width: 44 }}>
                        <input type="checkbox" checked={pendingRows.length > 0 && selectedIds.size === pendingRows.length} onChange={toggleSelectAll} style={{ accentColor: teal }} aria-label="Select all" />
                      </th>
                      {PENDING_COLUMNS.map(({ key, label, sort, search }) => (
                        <th
                          key={key}
                          className="text-nowrap"
                          style={{
                            padding: "10px 12px",
                            fontWeight: 600,
                            color: "#334155",
                            borderBottom: sortBy === key ? `2px solid ${teal}` : undefined,
                          }}
                        >
                          {label}
                          {sort && <SortIcon columnKey={key} />}
                          {search && <i className="fa fa-search ms-1" style={{ fontSize: "11px", color: "#94a3b8" }} aria-hidden />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRows.length === 0 ? (
                      <tr>
                        <td colSpan={PENDING_COLUMNS.length + 1} style={{ padding: "48px 24px", verticalAlign: "middle" }}>
                          <div className="text-center">
                            <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "42px", color: "#f59e0b", marginBottom: "8px" }} aria-hidden />
                            <div style={{ fontWeight: 600, color: "#334155" }}>No data available.</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pendingRows.map((row) => (
                        <tr key={row.id}>
                          <td style={{ padding: "10px 12px" }}>
                            <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelectOne(row.id)} style={{ accentColor: teal }} />
                          </td>
                          <td style={{ padding: "10px 12px" }}>{row.clientCreatedAt}</td>
                          <td style={{ padding: "10px 12px" }}>{row.clientName}</td>
                          <td style={{ padding: "10px 12px" }}>{row.email}</td>
                          <td style={{ padding: "10px 12px" }}>{row.relationshipManager || "—"}</td>
                          <td style={{ padding: "10px 12px" }}>{row.subBroker || "—"}</td>
                          <td style={{ padding: "10px 12px" }}>{row.aum ?? "—"}</td>
                          <td style={{ padding: "10px 12px", color: row.status === "Pending" ? "#dc2626" : undefined }}>{row.status}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Table - Submission Logs */}
            {activeTab === "logs" && (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-bordered mb-0" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f8fafc" }}>
                        {LOGS_COLUMNS.map(({ key, label, sort, search }) => (
                          <th
                            key={key}
                            className="text-nowrap"
                            style={{
                              padding: "10px 12px",
                              fontWeight: 600,
                              color: "#334155",
                              borderBottom: sortBy === key ? `2px solid ${teal}` : undefined,
                            }}
                          >
                            {label}
                            {sort && <SortIcon columnKey={key} />}
                            {search && <i className="fa fa-search ms-1" style={{ fontSize: "11px", color: "#94a3b8" }} aria-hidden />}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {logsRows.length === 0 ? (
                        <tr>
                          <td colSpan={LOGS_COLUMNS.length} style={{ padding: "48px 24px", verticalAlign: "middle" }}>
                            <div className="text-center">
                              <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "42px", color: "#f59e0b", marginBottom: "8px" }} aria-hidden />
                              <div style={{ fontWeight: 600, color: "#334155" }}>No data available.</div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((row) => (
                          <tr key={row.id}>
                            <td style={{ padding: "10px 12px" }}>{row.dateOfSubmission}</td>
                            <td style={{ padding: "10px 12px" }}>{row.clientName}</td>
                            <td style={{ padding: "10px 12px" }}>{row.email}</td>
                            <td style={{ padding: "10px 12px" }}>{row.relationshipManager || "—"}</td>
                            <td style={{ padding: "10px 12px" }}>{row.subBroker || "—"}</td>
                            <td style={{ padding: "10px 12px" }}>{row.aum ?? "—"}</td>
                            <td style={{ padding: "10px 12px" }}>{row.riskCategory || "—"}</td>
                            <td style={{ padding: "10px 12px", borderStyle: "dotted" }}>{row.suggestedRiskCategory ?? "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {logsRows.length > 0 && (
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2" style={{ borderTop: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
                    <div className="d-flex align-items-center gap-2">
                      <select
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        style={{ padding: "4px 8px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "13px" }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span style={{ fontSize: "13px", color: "#64748b" }}>rows visible out of {totalRows}.</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        className="border-0 rounded d-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, background: teal, color: "#fff", fontWeight: 700, fontSize: "14px", cursor: currentPage <= 1 ? "not-allowed" : "pointer", opacity: currentPage <= 1 ? 0.5 : 1 }}
                      >
                        ‹
                      </button>
                      <span style={{ padding: "0 8px", fontSize: "13px" }}>Page {currentPage} of {totalPages}</span>
                      <button
                        type="button"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        className="border-0 rounded d-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, background: teal, color: "#fff", fontWeight: 700, fontSize: "14px", cursor: currentPage >= totalPages ? "not-allowed" : "pointer", opacity: currentPage >= totalPages ? 0.5 : 1 }}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
